import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { exitUrl } from './exitUrl';
import {
  clearRegistrationContext,
  loadRegistrationContext,
  saveRegistrationContext
} from './registrationContext';
import { transition } from './registrationFlows';
import type {
  AccountState,
  FlowEvent,
  RegistrationContext,
  Step
} from './types';

export function useRegistrationFlow(): {
  context: RegistrationContext | null;
  step: Step;
  send: (event: FlowEvent, patch?: Partial<AccountState>) => void;
} {
  const navigate = useNavigate();
  const [context, setContext] = useState<RegistrationContext | null>(
    loadRegistrationContext
  );

  const send = useCallback(
    (event: FlowEvent, patch?: Partial<AccountState>) => {
      // Read storage rather than state so back-to-back sends see each other.
      const current = loadRegistrationContext() ?? context;
      if (!current) return;
      const next = transition(current, event, patch);
      if (next.exit) {
        const target = exitUrl(next.exit, current);
        clearRegistrationContext();
        if (target.kind === 'internal') {
          navigate(target.path, { replace: true });
        } else {
          window.location.assign(target.url);
        }
        return;
      }
      if (next.ctx !== current) {
        saveRegistrationContext(next.ctx);
        setContext(next.ctx);
      }
    },
    [context, navigate]
  );

  return { context, step: context?.account?.step ?? 'createAccount', send };
}
